import { TFunction } from 'i18next';

import {
  ErpSurveysListQuery,
  ErpSurveyDetailQuery,
  ErpGroupSurveyAssignmentsQuery,
  ErpSurveyAssignmentResultsQuery,
  ErpSurveyResponseDetailQuery,
  ErpSurveyTargetingQuery,
  ErpSurveyTargetGroupsQuery,
  ErpSurveySummaryQuery,
  ErpTeacherSurveyFeedbackQuery,
  ErpTeacherSurveyLeaderboardQuery,
  SaveSurveyTargetingMutation,
  Maybe,
} from 'core/graphql';

/**
 * Spec 0011 — shared survey domain model: wire types re-exported from the
 * generated hooks, the plain-String `questionType` vocabulary, and the
 * JSONString answer-value transform. JSON parsing of answer values lives HERE
 * (the hooks/transform layer) — components must never `JSON.parse` themselves.
 */

/* ---------- wire types ---------- */

export type ISurveyListItem = ErpSurveysListQuery['surveys'][number];
export type ISurveyDetail = NonNullable<ErpSurveyDetailQuery['survey']>;
export type ISurveyQuestion = ISurveyDetail['questions'][number];
export type IGroupSurveyAssignment = ErpGroupSurveyAssignmentsQuery['groupSurveyAssignments'][number];
export type ISurveyAssignmentResults = NonNullable<
  ErpSurveyAssignmentResultsQuery['surveyAssignmentResults']
>;
export type ISurveyRespondent = ISurveyAssignmentResults['respondents'][number];
export type ISurveyQuestionResult = ISurveyAssignmentResults['questionResults'][number];
export type ISurveyDistributionBucket = ISurveyQuestionResult['distribution'][number];
export type ISurveyResponseDetailWire = NonNullable<
  ErpSurveyResponseDetailQuery['surveyResponseDetail']
>;

/* ---------- targeting (spec 0027) ---------- */

export type ISurveyTargeting = NonNullable<
  ErpSurveyTargetingQuery['surveyTargeting']
>;
export type ISurveyPlan = ISurveyTargeting['plans'][number];
export type ISurveyPlanGroupRef = ISurveyPlan['groups'][number];
export type ISurveyTargetGroup = ErpSurveyTargetGroupsQuery['surveyTargetGroups'][number];
export type ISurveyTargetingResult = NonNullable<
  NonNullable<SaveSurveyTargetingMutation['setSurveyTargeting']>['targeting']
>;
export type ISurveyTargetingSkip = ISurveyTargetingResult['skipped'][number];

export type ISurveySummary = NonNullable<
  ErpSurveySummaryQuery['surveySummary']
>;
export type ISurveyGroupBreakdownRow = ISurveySummary['groupBreakdown'][number];

export type ITeacherSurveyReport = NonNullable<
  ErpTeacherSurveyFeedbackQuery['teacherSurveyFeedback']
>;
export type ITeacherRatingPoint = ITeacherSurveyReport['trend'][number];
export type ITeacherRatingBucket = ITeacherSurveyReport['byCourse'][number];
export type ITeacherLeaderboardRow = ErpTeacherSurveyLeaderboardQuery['teacherSurveyLeaderboard'][number];

/**
 * Plain lowercase Strings on the wire — the backend deliberately does NOT use
 * graphene enums here (they would arrive UPPERCASED and every comparison below
 * would silently stop matching).
 */
export const SURVEY_PLAN_SCOPES = ['groups', 'course'] as const;
export type SurveyPlanScope = typeof SURVEY_PLAN_SCOPES[number];

export const SURVEY_TRIGGER_KINDS = ['lesson_number', 'last_lesson'] as const;
export type SurveyTriggerKind = typeof SURVEY_TRIGGER_KINDS[number];

/** Per-group outcomes reported alongside a SUCCESSFUL save. */
export const SURVEY_SKIP_REASONS = [
  'NO_LESSON',
  'LESSON_OVER',
  'COURSE_MISMATCH',
  'ADOPTED',
  'FOREIGN_PLAN',
] as const;
export type SurveySkipReason = typeof SURVEY_SKIP_REASONS[number];

/** `ADOPTED` means "the row already existed and the rule took it over" — an
 * informational outcome, not something to warn the manager about. */
export const isNoisySkipReason = (reason: string): boolean =>
  reason !== 'ADOPTED';

export const surveySkipReasonMessage = (t: TFunction, reason: string): string =>
  (SURVEY_SKIP_REASONS as readonly string[]).includes(reason)
    ? t(`pages.surveys.skipReasons.${reason}`)
    : reason;

/** Draft shape the builder's Formik state holds for one targeting rule. */
export interface ISurveyPlanDraft {
  key: string;
  id?: string;
  /**
   * Names of the groups the rule already points at, keyed by id. The picker
   * only knows the groups on its CURRENT page (filtered by `onlyActive`,
   * search and lesson number), so without these an attached group that falls
   * outside that page rendered as a raw UUID chip.
   */
  groupNames?: Record<string, string>;
  scope: SurveyPlanScope;
  courseId?: string;
  onlyActiveGroups: boolean;
  triggerKind: SurveyTriggerKind;
  triggerLessonNumber?: number;
  skipPastLessons: boolean;
  isActive: boolean;
  groupIds: string[];
  hasResponses: boolean;
}

export const emptyPlanDraft = (
  key: string,
  courseId?: string,
): ISurveyPlanDraft => ({
  key,
  scope: 'groups',
  courseId: courseId || undefined,
  onlyActiveGroups: true,
  triggerKind: 'lesson_number',
  triggerLessonNumber: 1,
  // OFF for an explicit list (preserves today's «прикрепить к только что
  // прошедшему занятию»), flipped ON by the UI when the scope becomes
  // `course`, where a retro-attach would nag every finished group at once.
  skipPastLessons: false,
  isActive: true,
  groupIds: [],
  hasResponses: false,
});

export const planToDraft = (
  plan: ISurveyPlan,
  key: string,
): ISurveyPlanDraft => ({
  key,
  id: plan.id,
  scope: (plan.scope as SurveyPlanScope) || 'groups',
  courseId: plan.courseId || undefined,
  onlyActiveGroups: plan.onlyActiveGroups,
  triggerKind: (plan.triggerKind as SurveyTriggerKind) || 'lesson_number',
  triggerLessonNumber: plan.triggerLessonNumber ?? undefined,
  skipPastLessons: plan.skipPastLessons,
  isActive: plan.isActive,
  groupIds: plan.groups.map((group) => group.groupId),
  hasResponses: plan.hasResponses,
});

/* ---------- question types (plain String on the wire) ---------- */

export const SURVEY_QUESTION_TYPES = [
  'yes_no',
  'text',
  'rating',
  'single_choice',
  'multi_choice',
  'nps',
  'teacher_rating',
  'teacher_comment',
] as const;

export type SurveyQuestionTypeName = typeof SURVEY_QUESTION_TYPES[number];

export const isChoiceQuestionType = (questionType: string): boolean =>
  questionType === 'single_choice' || questionType === 'multi_choice';

/* ---------- customErrors → readable message ---------- */

/** The backend `SurveyServiceError` codes the UI translates explicitly. */
export const SURVEY_ERROR_CODES = [
  'SURVEY_LOCKED',
  'DUPLICATE_ASSIGNMENT',
  'COURSE_MISMATCH',
  'HAS_RESPONSES',
  'ARCHIVED',
  'LESSON_NOT_IN_GROUP',
  'INVALID_QUESTION',
  // spec 0027
  'PLAN_LOCKED',
  'INVALID_PLAN',
  'NOT_FOUND',
] as const;

interface ISurveyCustomError {
  code?: Maybe<string>;
  message?: Maybe<string>;
}

/**
 * Map the first customError onto a localized message: a known code resolves
 * through `pages.surveys.errors.<CODE>`; anything else falls back to the raw
 * backend message (or a generic error).
 */
export const surveyErrorMessage = (
  t: TFunction,
  customErrors?: Maybe<Array<Maybe<ISurveyCustomError>>>,
): string => {
  const first = (customErrors || []).find(Boolean);
  const code = first?.code || '';
  if ((SURVEY_ERROR_CODES as readonly string[]).includes(code)) {
    return t(`pages.surveys.errors.${code}`);
  }
  return first?.message || t('pages.surveys.errors.generic');
};

/* ---------- JSONString answer values ---------- */

/** Decoded answer value per spec 0011 §3.1 (bool / int / str / int[]). */
export type SurveyAnswerValue = boolean | number | string | number[] | null;

export const parseSurveyAnswerValue = (raw: string): SurveyAnswerValue => {
  try {
    return JSON.parse(raw) as SurveyAnswerValue;
  } catch {
    return null;
  }
};

export interface ISurveyParsedAnswer {
  questionId: string;
  value: SurveyAnswerValue;
}

export interface ISurveyResponseDetail {
  responseId: string;
  studentId: string;
  fullName: string;
  submittedAt: string;
  answers: ISurveyParsedAnswer[];
}

export const parseResponseDetail = (
  wire: ISurveyResponseDetailWire,
): ISurveyResponseDetail => ({
  responseId: wire.responseId,
  studentId: wire.studentId,
  fullName: wire.fullName,
  submittedAt: wire.submittedAt,
  answers: wire.answers.map((answer) => ({
    questionId: answer.questionId,
    value: parseSurveyAnswerValue(answer.value),
  })),
});

/**
 * Humanize a decoded answer for the individual-answers drawer. Returns the
 * pieces the component renders through i18n: `kind` selects the copy
 * (`yesNo` → Да/Нет keys), `text` carries the ready-made string otherwise.
 */
export const humanizeAnswer = (
  question: Pick<ISurveyQuestion, 'questionType' | 'options'>,
  value: SurveyAnswerValue,
):
  | { kind: 'yes' }
  | { kind: 'no' }
  | { kind: 'empty' }
  | { kind: 'text'; text: string } => {
  if (value === null || value === undefined) return { kind: 'empty' };
  switch (question.questionType) {
    case 'yes_no':
      return value === true ? { kind: 'yes' } : { kind: 'no' };
    case 'rating':
    case 'teacher_rating':
      return { kind: 'text', text: `${value} / 5` };
    case 'nps':
      return { kind: 'text', text: `${value} / 10` };
    case 'single_choice': {
      const option =
        typeof value === 'number' ? question.options[value] : undefined;
      return option ? { kind: 'text', text: option } : { kind: 'empty' };
    }
    case 'multi_choice': {
      const labels = Array.isArray(value)
        ? value
            .map((index) => question.options[index])
            .filter((label): label is string => Boolean(label))
        : [];
      return labels.length
        ? { kind: 'text', text: labels.join(', ') }
        : { kind: 'empty' };
    }
    case 'text':
    case 'teacher_comment':
    default:
      return typeof value === 'string' && value.trim()
        ? { kind: 'text', text: value }
        : { kind: 'empty' };
  }
};

/* ---------- NPS split (computed client-side per spec §3.2) ---------- */

export interface INpsSplit {
  promoters: number;
  passives: number;
  detractors: number;
}

/** promoters = 9–10, passives = 7–8, detractors = 1–6 (owner's decision). */
export const npsSplit = (
  distribution: readonly ISurveyDistributionBucket[],
): INpsSplit =>
  distribution.reduce<INpsSplit>(
    (acc, bucket) => {
      const score = Number(bucket.key);
      if (score >= 9) {
        return { ...acc, promoters: acc.promoters + bucket.count };
      }
      if (score >= 7) {
        return { ...acc, passives: acc.passives + bucket.count };
      }
      return { ...acc, detractors: acc.detractors + bucket.count };
    },
    { promoters: 0, passives: 0, detractors: 0 },
  );
